import * as THREE from 'three'

export interface Annotation {
  id: string
  position: THREE.Vector3
  text: string
  marker: THREE.Mesh
  label: THREE.Sprite
}

const sphereGeo = new THREE.SphereGeometry(0.04, 10, 10)
const sphereMat = new THREE.MeshBasicMaterial({ color: 0xff3333 })

let nextId = 1

export class AnnotationTool {
  annotations: Annotation[] = []
  private scene: THREE.Scene

  constructor(scene: THREE.Scene) {
    this.scene = scene
  }

  addAnnotation(position: THREE.Vector3, text = ''): Annotation {
    const id = `ann_${nextId++}`
    const marker = new THREE.Mesh(sphereGeo, sphereMat.clone())
    marker.position.copy(position)
    this.scene.add(marker)

    const label = this.createLabel(text || `标注 ${this.annotations.length + 1}`)
    label.position.copy(position)
    label.position.y += 0.08
    this.scene.add(label)

    const ann: Annotation = { id, position: position.clone(), text: text || `标注 ${this.annotations.length + 1}`, marker, label }
    this.annotations.push(ann)
    return ann
  }

  updateText(id: string, text: string) {
    const ann = this.annotations.find(a => a.id === id)
    if (!ann) return
    ann.text = text
    this.scene.remove(ann.label)
    ann.label = this.createLabel(text)
    ann.label.position.copy(ann.position)
    ann.label.position.y += 0.08
    this.scene.add(ann.label)
  }

  removeAnnotation(id: string) {
    const idx = this.annotations.findIndex(a => a.id === id)
    if (idx === -1) return
    const ann = this.annotations[idx]
    this.scene.remove(ann.marker)
    this.scene.remove(ann.label)
    this.annotations.splice(idx, 1)
  }

  getExportData(): { x: number; y: number; z: number; text: string }[] {
    return this.annotations.map(a => ({
      x: a.position.x, y: a.position.y, z: a.position.z, text: a.text,
    }))
  }

  clear() {
    for (const ann of this.annotations) {
      this.scene.remove(ann.marker)
      this.scene.remove(ann.label)
    }
    this.annotations = []
  }

  dispose() {
    this.clear()
  }

  private createLabel(text: string): THREE.Sprite {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')!
    const fontSize = 80
    const font = 'bold ' + fontSize + 'px Consolas, monospace'
    ctx.font = font
    const maxLineWidth = 600
    const lines = this.wrapText(ctx, text, maxLineWidth)
    const lineHeight = fontSize * 1.3
    const W = Math.min(Math.ceil(maxLineWidth + 40), 2048)
    const H = Math.max(Math.ceil(lines.length * lineHeight + 20), 128)
    canvas.width = W
    canvas.height = H
    ctx.clearRect(0, 0, W, H)
    ctx.fillStyle = '#ff3333'
    ctx.font = font
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    const startY = H / 2 - ((lines.length - 1) * lineHeight) / 2
    for (let i = 0; i < lines.length; i++) {
      ctx.fillText(lines[i], W / 2, startY + i * lineHeight)
    }
    const tex = new THREE.CanvasTexture(canvas)
    tex.minFilter = THREE.LinearFilter
    tex.magFilter = THREE.LinearFilter
    const mat = new THREE.SpriteMaterial({ map: tex, depthTest: false, depthWrite: false, sizeAttenuation: true, transparent: true })
    const sprite = new THREE.Sprite(mat)
    const aspect = W / H
    const spriteHeight = 0.5
    sprite.scale.set(spriteHeight * aspect, spriteHeight, 1)
    return sprite
  }

  private wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
    const words = text.split('')
    const lines: string[] = []
    let line = ''
    for (const char of words) {
      const testLine = line + char
      if (ctx.measureText(testLine).width > maxWidth && line.length > 0) {
        lines.push(line)
        line = char
      } else {
        line = testLine
      }
    }
    if (line) lines.push(line)
    return lines.length > 0 ? lines : [text]
  }
}
